import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CheckCircle2, Clock, Users, TrendingUp, Award, 
  Briefcase, Star, ArrowRight, Phone, Mail, 
  Calendar, Target, Zap, BookOpen, Globe, Shield,
  Play, ChevronRight
} from 'lucide-react';
import { trackPageView, trackButtonClick } from '../utils/analytics';

const CSDGPMLandingPage = () => {
  const navigate = useNavigate();
  const [discountTimeLeft, setDiscountTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    trackPageView('/csdgpm-landing', 'CSDGPM Landing Page - Limited Offer');

    // Discount/Enrollment ends on March 31st, 2026 at 11:59 PM
    const discountEndDate = new Date('2026-05-23T23:59:59');

    const timer = setInterval(() => {
      const now = new Date().getTime();
      const discountDistance = discountEndDate.getTime() - now;
      if (discountDistance > 0) {
        setDiscountTimeLeft({
          days: Math.floor(discountDistance / (1000 * 60 * 60 * 24)),
          hours: Math.floor((discountDistance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((discountDistance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((discountDistance % (1000 * 60)) / 1000)
        });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleEnrollClick = (location) => {
    trackButtonClick('enroll_now', location);
    navigate('/payment?utm_source=landing&utm_medium=csdgpm&utm_campaign=special_offer');
  };

  const handleEnquiryClick = (location) => {
    trackButtonClick('enquiry', location);
    navigate('/enrollment?utm_source=landing&utm_medium=csdgpm&utm_campaign=special_offer');
  };

  const reviews = [
    { name: "Rajesh Kumar", location: "Mumbai", text: "Got placed at a top MNC with 8 LPA package. The practical approach made all the difference." },
    { name: "Priya Sharma", location: "Dubai", text: "Working as Performance Marketing Manager now. The certification opened doors internationally." },
    { name: "Mohammed Al Zaabi", location: "UAE", text: "Transitioned from traditional marketing to digital. Now managing multi-million dollar campaigns." },
    { name: "Sneha Patel", location: "Bangalore", text: "Got 3 job offers before completing. The placement assistance was top-notch!" },
    { name: "Amit Desai", location: "Delhi", text: "Campaign ROI improved by 250%. Exemplar certification adds immense credibility." },
    { name: "Fatima Hassan", location: "Abu Dhabi", text: "Started freelancing and earning $3000+ monthly within 2 months of completion." },
  ];

  const curriculum = [
    { num: "01", title: "Digital Marketing Fundamentals", desc: "Marketing Evolution, Consumer Behavior, Channel Overview" },
    { num: "02", title: "Search Engine Optimization", desc: "On-Page SEO, Technical SEO, Link Building Strategies" },
    { num: "03", title: "Search Engine Marketing", desc: "Google Ads Mastery, Keyword Research, Campaign Optimization" },
    { num: "04", title: "Social Media Marketing", desc: "Facebook, Instagram, LinkedIn Ads & Analytics" },
    { num: "05", title: "Performance Marketing", desc: "CRO, A/B Testing, Funnel Optimization, ROI Measurement" },
    { num: "06", title: "AI-Powered Automation", desc: "Marketing Automation, Chatbots, Predictive Analytics" },
    { num: "07", title: "Analytics & Data", desc: "Google Analytics 4, Dashboard Creation, Reporting" },
    { num: "08", title: "Live Projects", desc: "Real Client Campaigns, Budget Management, Presentations" },
  ];

  const faqs = [
    { q: "Is this course suitable for beginners?", a: "Absolutely! Our course is designed for all levels - from complete beginners to experienced marketers. We start with fundamentals and progressively move to advanced concepts." },
    { q: "What is the duration of the course?", a: "The complete course duration is 6 months with flexible learning options. You can complete it faster with our accelerated track." },
    { q: "Is the certification globally recognized?", a: "Yes! We provide Exemplar Global certification which is recognized worldwide, significantly enhancing your credibility and job prospects." },
    { q: "Do you provide placement assistance?", a: "Yes! We offer 100% placement assistance including resume building, interview preparation, and job matching. Our team works until you get placed." },
    { q: "Can I work while doing this course?", a: "Absolutely! The course is designed for working professionals with flexible timings. All classes are recorded for your convenience." },
  ];

  // Dark Blue Color Palette
  const colors = {
    primary: '#1E3A5F',      // Dark Navy Blue
    secondary: '#2C5282',    // Medium Blue
    accent: '#3182CE',       // Bright Blue
    light: '#EBF8FF',        // Light Blue
    dark: '#1A365D',         // Darker Blue
    text: '#2D3748',         // Dark Text
    muted: '#718096'         // Muted Text
  };

  return (
    <div className="min-h-screen bg-white">
      
      {/* NAVIGATION - Christmas Theme */}
      <nav className="sticky top-0 left-0 right-0 z-40" style={{ background: 'linear-gradient(90deg, #1a472a, #0d2818)', borderBottom: '2px solid rgba(255, 215, 0, 0.3)' }}>
        <div className="container mx-auto max-w-6xl px-6 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="text-2xl font-serif font-bold text-white">
              🎄 AI School
            </a>
            <div className="hidden md:flex items-center gap-10">
              <a href="#program" className="text-sm font-medium text-white/80 hover:text-yellow-300">The Program</a>
              <a href="#curriculum" className="text-sm font-medium text-white/80 hover:text-yellow-300">Curriculum</a>
              <a href="#reviews" className="text-sm font-medium text-white/80 hover:text-yellow-300">Reviews</a>
              <a href="#faq" className="text-sm font-medium text-white/80 hover:text-yellow-300">FAQ</a>
            </div>
            <button
              onClick={() => handleEnrollClick('nav')}
              className="px-6 py-3 rounded-full font-bold text-sm hover:opacity-90"
              style={{ background: 'linear-gradient(90deg, #c41e3a, #8b0000)', color: 'white', boxShadow: '0 0 20px rgba(196, 30, 58, 0.4)' }}
            >
              🎁 Enroll Now
            </button>
          </div>
        </div>
      </nav>

      {/* CHRISTMAS HERO SECTION */}
      <section className="relative pt-8 pb-16 overflow-hidden" style={{ background: 'linear-gradient(135deg, #1a472a 0%, #0d2818 50%, #0a1f14 100%)' }}>
        {/* Snowflakes Animation */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(40)].map((_, i) => (
            <div
              key={i}
              className="absolute text-white opacity-60"
              style={{
                left: `${Math.random() * 100}%`,
                top: `-20px`,
                fontSize: `${Math.random() * 20 + 10}px`,
                animation: `snowfall ${Math.random() * 5 + 5}s linear infinite`,
                animationDelay: `${Math.random() * 5}s`
              }}
            >
              ❄
            </div>
          ))}
        </div>

        {/* Christmas Trees */}
        <div className="absolute left-0 bottom-0 opacity-15 pointer-events-none">
          <svg width="200" height="350" viewBox="0 0 200 300" fill="none">
            <path d="M100 0 L150 80 L130 80 L170 150 L140 150 L190 250 L10 250 L60 150 L30 150 L70 80 L50 80 Z" fill="#0f3d1f"/>
            <rect x="85" y="250" width="30" height="50" fill="#4a2c2a"/>
          </svg>
        </div>
        <div className="absolute right-0 bottom-0 opacity-15 pointer-events-none">
          <svg width="250" height="400" viewBox="0 0 200 300" fill="none">
            <path d="M100 0 L150 80 L130 80 L170 150 L140 150 L190 250 L10 250 L60 150 L30 150 L70 80 L50 80 Z" fill="#0f3d1f"/>
            <rect x="85" y="250" width="30" height="50" fill="#4a2c2a"/>
          </svg>
        </div>

        {/* Glowing Ornaments */}
        <div className="absolute top-32 left-20 w-4 h-4 rounded-full bg-red-500 shadow-lg shadow-red-500/50 animate-pulse"></div>
        <div className="absolute top-48 right-32 w-3 h-3 rounded-full bg-yellow-400 shadow-lg shadow-yellow-400/50 animate-pulse"></div>
        <div className="absolute top-64 left-40 w-3 h-3 rounded-full bg-green-400 shadow-lg shadow-green-400/50 animate-pulse"></div>
        <div className="absolute bottom-32 right-20 w-4 h-4 rounded-full bg-red-500 shadow-lg shadow-red-500/50 animate-pulse"></div>

        <div className="container mx-auto max-w-6xl px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              {/* Christmas Badge */}
              <div className="inline-flex items-center gap-3 px-5 py-2 rounded-full" style={{ background: 'linear-gradient(90deg, #c41e3a, #8b0000)', boxShadow: '0 0 25px rgba(196, 30, 58, 0.5)' }}>
                <span className="text-xl">🎄</span>
                <span className="text-white font-bold">CHRISTMAS SPECIAL</span>
                <span className="text-xl">🎁</span>
              </div>

              <div className="inline-flex items-center gap-3">
                <img 
                  src="https://customer-assets.emergentagent.com/job_ai-workshops/artifacts/sp9brvsf_exempler%20logo.webp" 
                  alt="Exemplar Global" 
                  className="h-10 brightness-0 invert" decoding="async" fetchPriority="high" loading="eager" />
                <span className="text-base font-semibold text-white/80">Globally Certified Program</span>
              </div>

              <h1 className="text-5xl lg:text-6xl font-serif leading-[1.1]">
                <span className="text-white" style={{ textShadow: '0 0 30px rgba(255,255,255,0.2)' }}>Transform your career in</span>
                <span className="block italic" style={{ 
                  background: 'linear-gradient(90deg, #ffd700, #ffec8b, #ffd700)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  filter: 'drop-shadow(0 0 20px rgba(255, 215, 0, 0.4))'
                }}>Digital Marketing</span>
              </h1>

              <p className="text-xl leading-relaxed max-w-lg text-white/80">
                Master AI-powered performance marketing and get placed in top companies with our 6-month specialist program.
              </p>

              <div className="pt-2 flex flex-wrap gap-4">
                <button
                  onClick={() => handleEnrollClick('hero_main')}
                  className="px-8 py-4 rounded-full font-bold text-lg hover:scale-105 transition-all inline-flex items-center gap-2"
                  style={{ 
                    background: 'linear-gradient(90deg, #ffd700, #ffcc00)',
                    color: '#1a472a',
                    boxShadow: '0 0 30px rgba(255, 215, 0, 0.5)'
                  }}
                >
                  🎁 Enroll Now — 50% OFF
                </button>
                <button
                  onClick={() => handleEnrollClick('hero_seat')}
                  className="px-8 py-4 rounded-full font-bold text-lg hover:scale-105 transition-all inline-flex items-center gap-2 border-2"
                  style={{ borderColor: '#ffd700', color: '#ffd700' }}
                >
                  Block Seat @ $20
                </button>
              </div>

              <div className="flex items-center gap-8 pt-4">
                <div>
                  <p className="text-2xl font-bold text-yellow-400">30K+</p>
                  <p className="text-sm text-white/60">Students</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-yellow-400">95%</p>
                  <p className="text-sm text-white/60">Placement</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-yellow-400">4.9★</p>
                  <p className="text-sm text-white/60">Rating</p>
                </div>
              </div>
            </div>

            <div className="hidden lg:block relative -mt-8">
              <img 
                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=500&fit=crop&q=80"
                alt="Performance Marketing Dashboard"
                className="w-full h-[420px] object-cover rounded-2xl shadow-2xl"
                style={{ boxShadow: '0 0 40px rgba(255, 215, 0, 0.2)' }} decoding="async" loading="lazy" />
              <div className="absolute -bottom-4 -left-4 bg-white p-5 rounded-xl shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center bg-green-100">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Avg. Salary Hike</p>
                    <p className="text-xl font-bold text-gray-900">↑ 150%</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CSS for snowfall */}
        <style>{`
          @keyframes snowfall {
            0% { transform: translateY(-20px) rotate(0deg); opacity: 0; }
            10% { opacity: 0.6; }
            90% { opacity: 0.6; }
            100% { transform: translateY(100vh) rotate(360deg); opacity: 0; }
          }
        `}</style>
      </section>

      {/* ============================================== */}
      {/* FESTIVE SPECIAL ENROLLMENT SECTION - BIG & ATTRACTIVE */}
      {/* ============================================== */}
      <section className="relative py-16 overflow-hidden" style={{ backgroundColor: colors.dark }}>
        {/* Animated Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full" style={{
            backgroundImage: 'radial-gradient(circle at 20% 50%, #3182CE 0%, transparent 50%), radial-gradient(circle at 80% 50%, #2C5282 0%, transparent 50%)'
          }}></div>
        </div>

        {/* Ticker Animation */}
        <div className="absolute top-0 left-0 right-0 overflow-hidden py-3" style={{ backgroundColor: colors.accent }}>
          <div className="animate-marquee whitespace-nowrap flex">
            {[...Array(10)].map((_, i) => (
              <span key={i} className="text-white font-bold text-sm mx-8">
                🎉 FESTIVE SPECIAL — BOOK YOUR SLOT @ JUST $20 USD — BATCH STARTS 1ST MARCH — 150 LIVE LEARNING HOURS — ENROLL NOW 🎉
              </span>
            ))}
          </div>
        </div>

        <div className="container mx-auto max-w-5xl px-6 pt-12 relative z-10">
          {/* Main Content */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full mb-6" style={{ backgroundColor: colors.accent }}>
              <span className="text-white text-lg font-bold">🎄 SEASON ENDING & FESTIVE SPECIAL 🎄</span>
            </div>
            
            <h2 className="text-4xl lg:text-6xl font-serif text-white mb-4">
              Book Your Slot @
              <span className="block text-6xl lg:text-8xl font-black mt-2" style={{ color: '#FCD34D' }}>JUST $20 USD</span>
            </h2>
            
            <p className="text-xl text-blue-200 max-w-2xl mx-auto mt-6">
              Join batches starting from <span className="font-bold text-white">New Batches Every Month</span> • 
              <span className="font-bold text-yellow-300"> 150 Live Learning Hours</span> with Expert Trainers
            </p>
          </div>

          {/* Countdown Timer */}
          <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 max-w-3xl mx-auto mb-10">
            <p className="text-center text-blue-200 text-sm mb-4">⏰ 50% DISCOUNT ENDS ON 17TH MAY</p>
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-5xl lg:text-6xl font-black text-white">{discountTimeLeft.days}</div>
                <p className="text-blue-200 text-sm mt-1">Days</p>
              </div>
              <div className="text-center">
                <div className="text-5xl lg:text-6xl font-black text-white">{discountTimeLeft.hours}</div>
                <p className="text-blue-200 text-sm mt-1">Hours</p>
              </div>
              <div className="text-center">
                <div className="text-5xl lg:text-6xl font-black text-white">{discountTimeLeft.minutes}</div>
                <p className="text-blue-200 text-sm mt-1">Minutes</p>
              </div>
              <div className="text-center">
                <div className="text-5xl lg:text-6xl font-black text-white">{discountTimeLeft.seconds}</div>
                <p className="text-blue-200 text-sm mt-1">Seconds</p>
              </div>
            </div>
          </div>

          {/* Key Dates */}
          <div className="flex flex-wrap justify-center gap-4 mb-10">
            <div className="flex items-center gap-2 bg-white/20 rounded-full px-5 py-2">
              <Calendar className="w-5 h-5 text-yellow-300" />
              <span className="text-white text-sm">Status: <span className="font-bold text-yellow-300">New Batches Every Month</span></span>
            </div>
            <div className="flex items-center gap-2 bg-white/20 rounded-full px-5 py-2">
              <Clock className="w-5 h-5 text-yellow-300" />
              <span className="text-white text-sm">Enrollment Closes: <span className="font-bold text-yellow-300">New Batches Every Month</span></span>
            </div>
            <div className="flex items-center gap-2 bg-white/20 rounded-full px-5 py-2">
              <Zap className="w-5 h-5 text-yellow-300" />
              <span className="text-white text-sm">Discount Valid: <span className="font-bold text-yellow-300">Till New Batches Every Month</span></span>
            </div>
          </div>

          {/* CTA Button */}
          <div className="text-center">
            <button
              onClick={() => handleEnrollClick('festive_cta')}
              className="px-12 py-5 rounded-full font-black text-xl hover:scale-105 transition-all shadow-2xl"
              style={{ backgroundColor: '#FCD34D', color: colors.dark }}
            >
              🎯 BOOK YOUR SLOT @ $20 NOW →
            </button>
            <p className="text-blue-200 text-sm mt-4">Limited Seats • 100% Placement Assistance • Exemplar Global Certification</p>
          </div>
        </div>

        {/* CSS for marquee animation */}
        <style>{`
          @keyframes marquee {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .animate-marquee {
            animation: marquee 20s linear infinite;
          }
        `}</style>
      </section>

      {/* THE OPPORTUNITY SECTION */}
      <section className="py-20 px-6" style={{ backgroundColor: colors.primary }}>
        <div className="container mx-auto max-w-4xl text-center">
          <p className="text-sm font-semibold tracking-wider uppercase mb-6" style={{ color: colors.accent }}>The Opportunity</p>
          <h2 className="text-4xl lg:text-5xl font-serif text-white mb-8 leading-tight">
            Digital Marketing is booming,
            <span className="block italic" style={{ color: '#FCD34D' }}>and skilled professionals are in demand</span>
          </h2>
          <p className="text-xl text-blue-200 leading-relaxed max-w-2xl mx-auto">
            The industry is projected to reach $786 billion by 2026. Companies are desperately seeking certified professionals who can drive growth through data-driven marketing.
          </p>

          <div className="grid grid-cols-3 gap-8 mt-16">
            <div className="text-center">
              <p className="text-5xl font-bold text-white mb-2">$786B</p>
              <p className="text-blue-200">Global Market by 2026</p>
            </div>
            <div className="text-center">
              <p className="text-5xl font-bold text-white mb-2">860K+</p>
              <p className="text-blue-200">Job Openings in India</p>
            </div>
            <div className="text-center">
              <p className="text-5xl font-bold text-white mb-2">45%</p>
              <p className="text-blue-200">Avg. Salary Growth</p>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS SECTION */}
      <section id="program" className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold tracking-wider uppercase mb-4" style={{ color: colors.accent }}>How It Works</p>
            <h2 className="text-4xl lg:text-5xl font-serif" style={{ color: colors.primary }}>
              Your path to
              <span className="italic" style={{ color: colors.accent }}> success</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-14 h-14 rounded-full border-2 flex items-center justify-center text-xl font-serif" style={{ borderColor: colors.accent, color: colors.accent }}>1</div>
              </div>
              <div>
                <h3 className="text-2xl font-serif mb-3" style={{ color: colors.primary }}>Expert-Led Live Classes</h3>
                <p style={{ color: colors.muted }}>Learn from industry professionals with 10+ years of experience at top companies like Google, Meta, and Amazon.</p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-14 h-14 rounded-full border-2 flex items-center justify-center text-xl font-serif" style={{ borderColor: colors.accent, color: colors.accent }}>2</div>
              </div>
              <div>
                <h3 className="text-2xl font-serif mb-3" style={{ color: colors.primary }}>Hands-On Projects</h3>
                <p style={{ color: colors.muted }}>Work on real campaigns with actual budgets. Build a portfolio that showcases your skills to employers.</p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-14 h-14 rounded-full border-2 flex items-center justify-center text-xl font-serif" style={{ borderColor: colors.accent, color: colors.accent }}>3</div>
              </div>
              <div>
                <h3 className="text-2xl font-serif mb-3" style={{ color: colors.primary }}>Global Certification</h3>
                <p style={{ color: colors.muted }}>Earn an Exemplar Global certification recognized in 100+ countries. Stand out in the job market.</p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-14 h-14 rounded-full border-2 flex items-center justify-center text-xl font-serif" style={{ borderColor: colors.accent, color: colors.accent }}>4</div>
              </div>
              <div>
                <h3 className="text-2xl font-serif mb-3" style={{ color: colors.primary }}>100% Placement Assistance</h3>
                <p style={{ color: colors.muted }}>Our dedicated team works with you until you land your dream job. Resume reviews, mock interviews, and direct referrals.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MEET YOUR TRAINER SECTION */}
      <section className="py-20 px-6" style={{ backgroundColor: colors.light }}>
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold tracking-wider uppercase mb-4" style={{ color: colors.accent }}>Meet Your Trainer</p>
            <h2 className="text-4xl lg:text-5xl font-serif" style={{ color: colors.primary }}>
              Learn from the
              <span className="italic" style={{ color: colors.accent }}> Best</span>
            </h2>
          </div>

          <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
            <div className="grid lg:grid-cols-2 gap-0">
              {/* Trainer Image */}
              <div className="relative">
                <img 
                  src="https://customer-assets.emergentagent.com/job_d9f28306-078b-4f0e-b01d-f0e5abc8a975/artifacts/zsv1ekd7_WhatsApp%20Image%202026-01-07%20at%2016.32.14.jpeg"
                  alt="Yamini Tak - Lead Trainer"
                  className="w-full h-full object-cover min-h-[400px]" decoding="async" loading="lazy" />
                <div className="absolute top-6 left-6">
                  <span className="bg-yellow-400 text-gray-900 px-4 py-2 rounded-full text-sm font-bold">⭐ Lead Trainer</span>
                </div>
              </div>

              {/* Trainer Info */}
              <div className="p-8 lg:p-12">
                <h3 className="text-3xl lg:text-4xl font-serif mb-2" style={{ color: colors.primary }}>Yamini Tak</h3>
                <p className="text-lg mb-6" style={{ color: colors.accent }}>Digital Marketing Expert & Performance Specialist</p>
                
                <p className="text-base leading-relaxed mb-8" style={{ color: colors.muted }}>
                  With over 10+ years of experience in digital marketing, Yamini Tak has trained thousands of professionals and helped them launch successful careers in the industry.
                </p>

                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                    <p className="text-sm" style={{ color: colors.text }}>Former Performance Marketing Lead at top MNCs with experience managing multi-million dollar ad budgets</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                    <p className="text-sm" style={{ color: colors.text }}>Google & Meta certified professional with expertise in ROI-driven campaigns</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                    <p className="text-sm" style={{ color: colors.text }}>Successfully mentored 5000+ students with a 95% placement rate</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                    <p className="text-sm" style={{ color: colors.text }}>Specialist in AI-powered marketing automation and data analytics</p>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 pt-6 border-t border-gray-200">
                  <div className="text-center">
                    <p className="text-2xl font-bold" style={{ color: colors.primary }}>10+</p>
                    <p className="text-xs" style={{ color: colors.muted }}>Years Exp.</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold" style={{ color: colors.primary }}>5000+</p>
                    <p className="text-xs" style={{ color: colors.muted }}>Students</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold" style={{ color: colors.primary }}>95%</p>
                    <p className="text-xs" style={{ color: colors.muted }}>Placements</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CURRICULUM SECTION */}
      <section id="curriculum" className="py-20 px-6" style={{ backgroundColor: colors.light }}>
        <div className="container mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div className="lg:sticky lg:top-32">
              <p className="text-sm font-semibold tracking-wider uppercase mb-4" style={{ color: colors.accent }}>Curriculum</p>
              <h2 className="text-4xl lg:text-5xl font-serif mb-6" style={{ color: colors.primary }}>
                8 comprehensive
                <span className="block italic" style={{ color: colors.accent }}>modules</span>
              </h2>
              <p className="text-lg mb-8" style={{ color: colors.muted }}>
                <span className="font-bold text-2xl" style={{ color: colors.primary }}>150 Live Learning Hours</span> designed to take you from beginner to industry-ready professional in 6 months.
              </p>
              <button
                onClick={() => handleEnrollClick('curriculum')}
                className="px-8 py-4 rounded-full text-white font-semibold hover:opacity-90 flex items-center gap-2"
                style={{ backgroundColor: colors.primary }}
              >
                Start Learning
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {curriculum.map((module, index) => (
                <div 
                  key={index} 
                  className="bg-white rounded-2xl p-6 hover:shadow-lg transition-all"
                >
                  <div className="flex items-start gap-4">
                    <span className="text-3xl font-serif" style={{ color: colors.accent }}>{module.num}</span>
                    <div>
                      <h3 className="text-lg font-semibold mb-1" style={{ color: colors.primary }}>{module.title}</h3>
                      <p className="text-sm" style={{ color: colors.muted }}>{module.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PRICING SECTION */}
      <section className="py-20 px-6" style={{ backgroundColor: colors.primary }}>
        <div className="container mx-auto max-w-5xl">
          {/* HUGE BLOCK YOUR SEAT SECTION */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-6 py-2 rounded-full mb-6" style={{ backgroundColor: '#FCD34D' }}>
              <span className="text-gray-900 text-sm font-bold">⚡ LIMITED TIME FESTIVE OFFER</span>
            </div>
            <h2 className="text-4xl lg:text-6xl font-serif text-white mb-6 leading-tight">
              Block Your Seat @
              <span className="block text-6xl lg:text-8xl font-black mt-2" style={{ color: '#FCD34D' }}>JUST $20</span>
            </h2>
            <p className="text-xl lg:text-2xl text-blue-200 max-w-3xl mx-auto">
              And get <span className="font-black text-yellow-300 text-3xl">50% OFF</span> on the complete course!
              <span className="block mt-2 text-white font-bold">This offer ends on New Batches Every Month — After that, NO discount!</span>
            </p>
          </div>

          {/* Countdown to Dec 31st */}
          <div className="bg-gradient-to-r from-red-600 to-orange-500 rounded-2xl p-6 max-w-2xl mx-auto mb-10">
            <p className="text-center text-white text-lg font-bold mb-4">⏰ 50% DISCOUNT ENDS IN:</p>
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-4xl lg:text-5xl font-black text-white">{discountTimeLeft.days}</div>
                <p className="text-white/80 text-sm mt-1">Days</p>
              </div>
              <div className="text-center">
                <div className="text-4xl lg:text-5xl font-black text-white">{discountTimeLeft.hours}</div>
                <p className="text-white/80 text-sm mt-1">Hours</p>
              </div>
              <div className="text-center">
                <div className="text-4xl lg:text-5xl font-black text-white">{discountTimeLeft.minutes}</div>
                <p className="text-white/80 text-sm mt-1">Minutes</p>
              </div>
              <div className="text-center">
                <div className="text-4xl lg:text-5xl font-black text-white">{discountTimeLeft.seconds}</div>
                <p className="text-white/80 text-sm mt-1">Seconds</p>
              </div>
            </div>
          </div>

          {/* Main Pricing Card */}
          <div className="bg-white rounded-3xl p-10 lg:p-12 shadow-2xl max-w-4xl mx-auto">
            
            {/* $20 Block Seat Highlight */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-8 mb-8 text-center text-white">
              <p className="text-lg font-bold mb-2">🎯 BLOCK YOUR SEAT TODAY</p>
              <div className="text-6xl lg:text-7xl font-black mb-2">$20</div>
              <p className="text-lg">Pay just $20 now to reserve your seat</p>
              <p className="text-sm opacity-90 mt-2">Balance payable before batches running (New Batches Every Month)</p>
            </div>

            {/* Full Course Price */}
            <div className="text-center mb-8">
              <p className="text-sm font-semibold uppercase tracking-wide mb-3" style={{ color: colors.muted }}>Complete Course Fee</p>
              <div className="flex items-center justify-center gap-4 mb-2">
                <span className="text-2xl text-gray-400 line-through">$599 USD</span>
                <span className="text-5xl lg:text-6xl font-serif font-bold" style={{ color: colors.accent }}>$299 USD</span>
              </div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mt-3" style={{ backgroundColor: '#FEF3C7' }}>
                <span className="text-sm font-bold text-amber-800">⚠️ Price goes back to $599 after New Batches Every Month!</span>
              </div>
            </div>

            <div className="w-full h-px bg-gray-200 my-8"></div>

            <div className="grid md:grid-cols-2 gap-8 mb-10">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: colors.light }}>
                  <Globe className="w-7 h-7" style={{ color: colors.accent }} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-1" style={{ color: colors.primary }}>Exemplar Global Certification</h3>
                  <p className="text-sm" style={{ color: colors.muted }}>Internationally recognized certification from a renowned global accreditation body.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: colors.light }}>
                  <Award className="w-7 h-7" style={{ color: colors.accent }} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-1" style={{ color: colors.primary }}>Google Ads Certification Prep</h3>
                  <p className="text-sm" style={{ color: colors.muted }}>100% preparation to clear the Google Paid Campaign Certification.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: colors.light }}>
                  <Briefcase className="w-7 h-7" style={{ color: colors.accent }} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-1" style={{ color: colors.primary }}>Job-Ready for Performance Marketing</h3>
                  <p className="text-sm" style={{ color: colors.muted }}>Eligible for latest high-paying jobs. 100% placement assistance until you're hired.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: colors.light }}>
                  <Target className="w-7 h-7" style={{ color: colors.accent }} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-1" style={{ color: colors.primary }}>Freelancing Opportunities</h3>
                  <p className="text-sm" style={{ color: colors.muted }}>Learn how to land high-paying freelance clients. Alumni earn $3000+ monthly.</p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl p-6 mb-10" style={{ backgroundColor: colors.light }}>
              <p className="text-sm font-semibold uppercase tracking-wide mb-4" style={{ color: colors.accent }}>Everything Included</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  '150 Live Learning Hours',
                  'Live Expert Sessions',
                  'Real Client Projects',
                  'AI-Powered Tools Training',
                  'Resume Building',
                  'Mock Interviews',
                  'Lifetime Community Access',
                  'Course Recordings',
                  'Doubt Clearing Sessions'
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: colors.accent }} />
                    <span className="text-sm" style={{ color: colors.primary }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <button
              onClick={() => handleEnrollClick('pricing_main')}
              className="w-full py-5 rounded-full font-black text-xl text-white hover:opacity-90 flex items-center justify-center gap-2"
              style={{ backgroundColor: '#10B981' }}
            >
              🎯 BLOCK YOUR SEAT @ $20 NOW
              <ArrowRight className="w-6 h-6" />
            </button>

            <p className="text-center text-sm mt-6" style={{ color: colors.muted }}>
              🔒 Secure payment • 30-day money-back guarantee • Join 30,000+ successful alumni
            </p>
          </div>
        </div>
      </section>

      {/* REVIEWS SECTION */}
      <section id="reviews" className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold tracking-wider uppercase mb-4" style={{ color: colors.accent }}>Success Stories</p>
            <h2 className="text-4xl lg:text-5xl font-serif" style={{ color: colors.primary }}>
              What our
              <span className="italic" style={{ color: colors.accent }}> alumni say</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((review, index) => (
              <div key={index} className="rounded-2xl p-6" style={{ backgroundColor: colors.light }}>
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="mb-6 italic" style={{ color: colors.muted }}>"{review.text}"</p>
                <div>
                  <p className="font-semibold" style={{ color: colors.primary }}>{review.name}</p>
                  <p className="text-sm" style={{ color: colors.muted }}>{review.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section id="faq" className="py-20 px-6" style={{ backgroundColor: colors.light }}>
        <div className="container mx-auto max-w-3xl">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold tracking-wider uppercase mb-4" style={{ color: colors.accent }}>FAQ</p>
            <h2 className="text-4xl lg:text-5xl font-serif" style={{ color: colors.primary }}>
              Common
              <span className="italic" style={{ color: colors.accent }}> questions</span>
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-2xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full p-6 text-left flex items-center justify-between"
                >
                  <span className="font-semibold text-lg" style={{ color: colors.primary }}>{faq.q}</span>
                  <ChevronRight 
                    className={`w-5 h-5 transition-transform ${openFaq === index ? 'rotate-90' : ''}`}
                    style={{ color: colors.accent }}
                  />
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6">
                    <p style={{ color: colors.muted }}>{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA SECTION */}
      <section className="py-20 px-6" style={{ backgroundColor: colors.dark }}>
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="text-4xl lg:text-5xl font-serif text-white mb-6">
            Ready to transform
            <span className="block italic" style={{ color: '#FCD34D' }}>your career?</span>
          </h2>
          <p className="text-xl text-blue-200 mb-8">
            Join 30,000+ successful alumni. Limited seats available.
          </p>
          <button
            onClick={() => handleEnrollClick('final_cta')}
            className="px-10 py-5 rounded-full font-bold text-lg hover:opacity-90"
            style={{ backgroundColor: '#FCD34D', color: colors.dark }}
          >
            Book Your Slot @ $20 Now
          </button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-12 px-6 bg-white border-t border-gray-100">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <span className="text-xl font-serif font-bold" style={{ color: colors.primary }}>AI School for All</span>
              <span className="text-sm" style={{ color: colors.muted }}>Powered by Workshop Library</span>
            </div>
            <img 
              src="https://customer-assets.emergentagent.com/job_ai-workshops/artifacts/sp9brvsf_exempler%20logo.webp" 
              alt="Exemplar Global" 
              className="h-8 opacity-60" decoding="async" loading="lazy" />
          </div>
          <p className="text-center text-sm mt-8" style={{ color: colors.muted }}>
            © 2026 AI School for All. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default CSDGPMLandingPage;
